import React, { useState } from "react";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import PropTypes from "prop-types";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import { useMediaQuery, useTheme } from "@mui/material";
import MerchantDocuments from "layouts/merchants/merchants-documents";
import { useDashboard } from "context/DashboardContext";

function TeamInfoCard({ title, description, merchantId, rowData }) {
  const {
    merchantDocuments,
    rejectDocuments,
    confirmMerchant,
    approveMerchant,
    loading,
  } = useDashboard();

  const [message, setMessage] = useState("");
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  const theme = useTheme();
  const isXSmallScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.down("lg"));

  const fileId = merchantDocuments?.[0]?.id;

  const isAccepted =
    rowData.merchant.approved && rowData.merchant.kycLevel === 3;

  // -------------------------
  // ACCEPT MERCHANT
  // -------------------------
  const handleConfirmMerchant = () => {
    if (!merchantId) return;

    confirmMerchant({
      merchantId: String(merchantId),
    });
  };

  const handleApproveMerchant = () => {
    if (!merchantId) return;

    approveMerchant({
      merchantId: String(merchantId),
    });
  };

  const handleRejectMerchant = async (event) => {
    event.preventDefault();
    if (!fileId || !message.trim()) return;

    await rejectDocuments({
      fileId,
      reason: message.trim(),
    });
    setMessage("");
    setRejectDialogOpen(false);
  };

  return (
    <Card sx={{ height: "100%" }}>
      {/* ACTION BUTTONS */}
      <Box
        display='flex'
        justifyContent='flex-end'
        alignItems='center'
        pt={2}
        px={2}
      >
        <Button
          variant='contained'
          sx={{
            mr: 1,
            fontSize: "12px",
            color: "#fff !important",
            width: isXSmallScreen
              ? "10%"
              : isSmallScreen
              ? "10%"
              : isMediumScreen
              ? "10%"
              : isLargeScreen
              ? "10%"
              : "10%",
          }}
          onClick={handleConfirmMerchant}
          disabled={isAccepted || loading}
        >
          Confirm
        </Button>

        <Button
          variant='contained'
          sx={{
            mr: 1,
            fontSize: "12px",
            color: "#fff !important",
            width: isXSmallScreen
              ? "10%"
              : isSmallScreen
              ? "10%"
              : isMediumScreen
              ? "10%"
              : isLargeScreen
              ? "10%"
              : "10%",
          }}
          onClick={handleApproveMerchant}
          disabled={isAccepted || loading}
        >
          Approve
        </Button>

        <Button
          variant='contained'
          sx={{
            fontSize: "12px",
            color: "#fff !important",
            width: isXSmallScreen
              ? "10%"
              : isSmallScreen
              ? "10%"
              : isMediumScreen
              ? "10%"
              : isLargeScreen
              ? "10%"
              : "10%",
          }}
          onClick={() => setRejectDialogOpen(true)}
          disabled={isAccepted || loading || !fileId}
        >
          Reject
        </Button>
      </Box>

      {/* TITLE */}
      <SoftBox px={2}>
        <SoftTypography variant='h6' fontWeight='medium'>
          {title}
        </SoftTypography>
      </SoftBox>

      <SoftBox p={2}>
        {/* DESCRIPTION */}
        <SoftTypography variant='caution' color='text'>
          {description}
        </SoftTypography>

        <SoftBox opacity={0.3} my={2}>
          <Divider />
        </SoftBox>

        {/* DOCUMENT LIST */}
        <MerchantDocuments merchantId={merchantId} merchantData={rowData} />
      </SoftBox>

      <Dialog
        open={rejectDialogOpen}
        onClose={() => {
          setRejectDialogOpen(false);
          setMessage("");
        }}
        fullWidth
        maxWidth='sm'
      >
        <form onSubmit={handleRejectMerchant}>
          <DialogTitle>Reject merchant document</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              required
              multiline
              minRows={3}
              margin='dense'
              size='small'
              label='Reason for rejection'
              fullWidth
              sx={{ "& .MuiInputLabel-root": { fontSize: "0.875rem" } }}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setRejectDialogOpen(false);
                setMessage("");
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type='submit'
              color='error'
              disabled={loading || !message.trim()}
            >
              Reject document
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Card>
  );
}

TeamInfoCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  merchantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default TeamInfoCard;
